/**
 * UpgradeModal Component
 *
 * Modal shown when user hits quota/limit errors
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import { X, Zap, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  upgradeUrl?: string;
  isAnonymousLimit?: boolean;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  open,
  onClose,
  title,
  message,
  upgradeUrl = '/subscription',
  isAnonymousLimit = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const benefits = isAnonymousLimit
    ? [
        t('quota.upgradeModal.benefits.unlimitedChatSessions'),
        t('quota.upgradeModal.benefits.includedMonthlyUsage'),
        t('quota.upgradeModal.benefits.customAgents'),
      ]
    : [
        t('quota.upgradeModal.benefits.higherMonthlyUsage'),
        t('quota.upgradeModal.benefits.prioritySupport'),
        t('quota.upgradeModal.benefits.advancedModels'),
      ];

  const handleUpgrade = () => {
    onClose();
    navigate(upgradeUrl === '/pricing' ? '/subscription' : upgradeUrl);
  };

  const handleRegister = () => {
    onClose();
    navigate('/', { state: { authMode: 'signup' } });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: 'rounded-lg',
      }}
    >
      <DialogTitle className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
        <Box className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-yellow-500" />
          <Typography variant="h6" className="font-semibold">
            {title ||
              (isAnonymousLimit ? t('quota.guestLimitTitle') : t('quota.quotaExceededTitle'))}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <X className="h-5 w-5" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="py-6">
        <Typography variant="body1" className="mb-4 text-gray-700 dark:text-gray-300">
          {message}
        </Typography>

        {/* Benefits list */}
        <Box className="mt-6">
          <Typography variant="body2" className="mb-3 font-semibold">
            {isAnonymousLimit
              ? t('quota.upgradeModal.guestBenefitsTitle')
              : t('quota.upgradeModal.upgradeBenefitsTitle')}
          </Typography>
          <Box className="space-y-2">
            {benefits.map((benefit) => (
              <Box key={benefit} className="flex items-start gap-2">
                <Zap className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                <Typography variant="body2" className="text-gray-600 dark:text-gray-400">
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions className="border-t border-gray-200 p-4 dark:border-gray-700">
        <Button onClick={onClose} variant="outlined" className="mr-2">
          {t('common.cancel')}
        </Button>
        {isAnonymousLimit ? (
          <Button
            onClick={handleRegister}
            variant="contained"
            color="primary"
            endIcon={<ArrowRight className="h-4 w-4" />}
          >
            {t('quota.upgradeModal.signUpFree')}
          </Button>
        ) : (
          <Button
            onClick={handleUpgrade}
            variant="contained"
            color="primary"
            endIcon={<ArrowRight className="h-4 w-4" />}
          >
            {t('quota.upgradeModal.viewPlans')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UpgradeModal;
